import { exists } from '@jack-henry/frontend-utils/functions';
import { formatCurrencyAtm, formatCurrencyComplete } from '@treasury/utils/functions';
import type { TextFieldValueChangedEvent } from '@vaadin/text-field';
import { css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { TmTextField } from './tm-text-field';

// eslint-disable-next-line no-duplicate-imports
import '@vaadin/text-field';

export const tagName = 'tm-currency-field';
@customElement(tagName)
export class TmCurrencyField extends TmTextField {
    @property()
    public placeholder = '0.00';

    @property({ type: Number })
    public maxLength?: number;

    @state()
    private displayValue = '';

    public firstUpdated() {
        this.formatInitialValue();
        this.formatPlaceholder();
    }

    protected render() {
        return html`
            <vaadin-text-field
                @value-changed=${(e: TextFieldValueChangedEvent) => {
                    this.onValueChanged(e);
                }}
                .value=${this.displayValue}
                allowed-char-pattern="[0-9]"
                class="w-full"
                part="component"
                .label=${this.label}
                placeholder=${this.placeholder}
                ?required=${this.required}
                ?readonly=${this.readonly}
                ?disabled=${this.disabled}
                ?invalid=${this.invalid}
                .maxlength=${this.maxLength}
            >
                <div slot="prefix" class="bg-transparent">$</div>
                <input slot="input" inputmode="numeric" class="my-[2px] ml-1 text-right" />
            </vaadin-text-field>
        `;
    }

    private formatInitialValue() {
        if (!exists(this.initial)) {
            if (exists(this.placeholder)) {
                return;
            } else {
                this.displayValue = '0.00';
            }
            return;
        }

        const parsed = Number.parseFloat(this.initial);
        if (isNaN(parsed)) {
            throw new Error(`Initial value "${this.initial}" is not a valid number`);
        }

        // respect initial value by formatting it differently than user input;,
        // e.g., "1" becomes "1.00" rather than "0.01";
        this.displayValue = formatCurrencyComplete(parsed, { symbol: '' });
    }

    private formatPlaceholder() {
        if (!this.placeholder) {
            return;
        }

        const placeholderIsNumber = !isNaN(Number.parseFloat(this.placeholder));
        if (placeholderIsNumber) {
            this.placeholder = formatCurrencyAtm(this.placeholder);
        }
    }

    private onValueChanged(e: TextFieldValueChangedEvent) {
        e.stopPropagation();
        const { value } = e.detail;

        // the value that comes out of component is formatted,
        // so we need to compare to the displayValue, not the raw value
        if (value === this.displayValue) {
            return;
        }

        this.displayValue = formatCurrencyAtm(value);

        // Android doesn't respect the allow-char-pattern so this is used to force the displayValue value into the input
        const input = this.shadowRoot?.querySelector('vaadin-text-field input') as HTMLInputElement;
        input.value = this.displayValue;

        // remove commas so the string can be parsed correctly by consumers
        const strippedValue = this.displayValue.replace(',', '');
        this.changeValue(strippedValue);
    }

    public static get styles() {
        return [
            ...super.styles,
            css`
                :host {
                    display: block;
                    --input-field-text: var(--brand-primary);
                }
                :host([theme='dark']) {
                    --input-field-text: var(--jh-color-gray-0);
                }
                vaadin-text-field > input {
                    -webkit-mask-image: none;
                    mask-image: none;
                }

                vaadin-text-field::part(input-field) {
                    border: none;
                    border-bottom: 1px solid var(--input-field-border);
                    background-color: var(--input-field-background);
                    border-radius: 4px 4px 0 0;
                    color: var(--input-field-text);
                }

                :host(:not([noouterspacing]))::part(component) {
                    padding-top: 0;
                }

                :host(.form-row-slotted) input {
                    text-align: right;
                }

                vaadin-text-field[invalid],
                vaadin-text-field[invalid] > [slot='prefix'] {
                    color: var(--content-negative);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmCurrencyField;
    }
}
